/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { genericSelectors } from '@opengeoweb/store';
import type {
  GeoJsonWithService,
  Parameter,
  PlotPreset,
  TimeSeriesLocation,
} from './types';
import { createGeoJsonArrays } from './utils';
import {
  getPlotState,
  getServices,
  getShouldHideLocationMarkers,
} from '../../store/selectors';
import type { TimeSeriesRootState } from '../../store/types';
import type { GetLocationsOption } from '../../utils/timeseries-api/hooks';
import { useGetLocations } from '../../utils/timeseries-api/hooks';

export const getUniqueServicesFromPreset = (
  parameters: Parameter[],
  services: TimeSeriesService[],
): GetLocationsOption[] => {
  if (!parameters.length || !services.length) {
    return [];
  }

  const options = parameters
    .map(({ serviceId, collectionId }) => {
      const service = services.find((item) => item.id === serviceId);
      return service ? { service, collectionId } : null;
    })
    .filter((item): item is GetLocationsOption => item !== null);

  return options.filter(
    (current, index, array) =>
      index ===
      array.findIndex(
        (item) =>
          item.service.id === current.service.id &&
          item.collectionId === current.collectionId,
      ),
  );
};

interface UseTimeSeriesLocationsResult {
  combinedGeoJson: GeoJSON.FeatureCollection;
  isLocationsFetching: boolean;
  serviceListForLocations: GetLocationsOption[];
  splitGeoJsonByCollection: GeoJsonWithService[];
}

export const useTimeSeriesLocations = (
  panelId: string,
): UseTimeSeriesLocationsResult => {
  const services = useSelector((state: TimeSeriesRootState) =>
    getServices(state),
  );
  const shouldHideLocationMarkers = useSelector((state: TimeSeriesRootState) =>
    getShouldHideLocationMarkers(state, panelId),
  );
  const plotPresetState: PlotPreset | undefined = useSelector(
    (state: TimeSeriesRootState) => getPlotState(state, panelId),
  );
  const linkedMapIds = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getLinkedTargetIds(state, panelId),
  );
  const panelMapId = linkedMapIds?.[0] ?? plotPresetState?.mapId;
  const selectedFeatureFromPanel = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getSelectedFeature(state, panelId),
  );
  const selectedFeatureFromMap = useSelector((state: TimeSeriesRootState) =>
    panelMapId ? genericSelectors.getSelectedFeature(state, panelMapId) : null,
  );
  const selectedFeature = selectedFeatureFromPanel ?? selectedFeatureFromMap;

  const memoizedSelectedFeature: TimeSeriesLocation | null =
    React.useMemo(() => {
      if (!selectedFeature) {
        return null;
      }

      return {
        lat: selectedFeature.lat,
        lon: selectedFeature.lon,
        id: selectedFeature.id,
        occurrences: selectedFeature.occurrences,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFeature?.lat, selectedFeature?.lon]);

  const parameters = React.useMemo(
    () => plotPresetState?.parameters || [],
    [plotPresetState?.parameters],
  );
  const stableServices = React.useMemo(() => services || [], [services]);

  const serviceListForLocations = React.useMemo(
    () => getUniqueServicesFromPreset(parameters, stableServices),
    [parameters, stableServices],
  );
  const { data: locationsData, isFetching: isLocationsFetching } =
    useGetLocations(serviceListForLocations);

  const locationsGeoJson = React.useMemo(
    () =>
      stableServices.length
        ? createGeoJsonArrays(
            locationsData,
            stableServices,
            shouldHideLocationMarkers,
            memoizedSelectedFeature,
          )
        : {
            combinedGeoJson: {
              type: 'FeatureCollection' as const,
              features: [],
            },
            splitGeoJsonByCollection: [],
          },
    [
      locationsData,
      memoizedSelectedFeature,
      shouldHideLocationMarkers,
      stableServices,
    ],
  );

  return {
    combinedGeoJson: locationsGeoJson.combinedGeoJson,
    isLocationsFetching,
    serviceListForLocations,
    splitGeoJsonByCollection: locationsGeoJson.splitGeoJsonByCollection,
  };
};
